import makeStyles from '@mui/styles/makeStyles';

import {
  borderColor,
  darkGray,
  surfaceElevated,
  surfaceSecondary,
  teal900,
  textPrimary,
} from '@/theme/colors';

export const useStyles = makeStyles({
  // Modal container
  paper: {
    margin: 'auto',
    padding: '0 !important',
  },

  // Form styles
  formContent: {
    padding: '16px 24px',
    backgroundColor: surfaceElevated,
  },
  nameRow: {
    display: 'flex',
    gap: 8,
  },
  // Agreement checkbox styles
  agreements: {
    marginTop: 8,
    textAlign: 'left',
  },
  // Footer styles
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 24px',
    borderTop: `1px solid ${borderColor}`,
    backgroundColor: surfaceSecondary,
  },
  backButton: {
    color: teal900,
    fontSize: 12,
    fontWeight: 500,
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  continueButton: {
    backgroundColor: teal900,
    color: 'white',
    fontSize: 14,
    fontWeight: 500,
    padding: '8px 24px !important',
    minHeight: '32px',
    textTransform: 'uppercase',
    '&:hover': {
      backgroundColor: '#004544',
    },
    '&:disabled': {
      backgroundColor: '#D1D5DB',
      color: '#9CA3AF',
    },
  },
  // ENG-5549: processing keeps the brand fill instead of the grey disabled look.
  continueButtonProcessing: {
    '&:disabled': {
      backgroundColor: teal900,
      color: 'white',
      // The teal Button variant fades every disabled state to 0.6.
      opacity: 1,
    },
  },
  backButtonDisabled: {
    fontSize: 12,
    fontWeight: 500,
    color: '#9CA3AF',
    cursor: 'default',
  },
  // Payment step styles
  cardOnFileNote: {
    fontSize: 14,
    color: darkGray,
    margin: '0 0 16px 0',
  },
  trialBanner: {
    backgroundColor: '#F0FDFA',
    border: '1px solid #99F6E4',
    borderRadius: 8,
    padding: '12px 16px',
    marginBottom: 16,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  trialBannerIcon: {
    color: teal900,
    flexShrink: 0,
    marginTop: 2,
  },
  trialBannerContent: {
    flex: 1,
  },
  trialBannerTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#0F766E',
    margin: 0,
  },
  trialBannerSubtitle: {
    fontSize: 12,
    color: '#0F766E',
    margin: '4px 0 0 0',
  },
  // Payment element styles
  paymentElementWrapper: {
    marginBottom: 16,
  },
  paymentElementLabel: {
    display: 'block',
    fontSize: 14,
    color: darkGray,
    marginBottom: 8,
  },
  paymentElementContainer: {
    border: `1px solid ${borderColor}`,
    borderRadius: 4,
    padding: 8,
    backgroundColor: surfaceElevated,
  },
  paymentError: {
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  paymentErrorText: {
    fontSize: 14,
    color: '#DC2626',
    margin: 0,
  },
  // Order Summary styles
  orderSummary: {
    backgroundColor: surfaceSecondary,
    border: `1px solid ${borderColor}`,
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  orderSummaryTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: textPrimary,
    margin: '0 0 12px 0',
  },
  orderSummaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  orderSummaryLabel: {
    fontSize: 14,
    color: darkGray,
  },
  orderSummaryValue: {
    fontSize: 14,
    fontWeight: 500,
    color: textPrimary,
  },
  orderSummaryExpert: {
    fontSize: 12,
    color: darkGray,
    margin: '0 0 8px 0',
  },
  orderSummaryDivider: {
    borderTop: `1px solid ${borderColor}`,
    paddingTop: 8,
    marginTop: 8,
  },
  orderSummaryTotal: {
    fontWeight: 600,
  },

  // Phone verification styles
  phoneInstructions: {
    fontSize: 14,
    color: darkGray,
    textAlign: 'center',
    margin: '0 0 16px 0',
  },
  loginLink: {
    textAlign: 'center',
    marginTop: 12,
  },
  loginLinkText: {
    fontSize: 14,
    color: teal900,
    textDecoration: 'none',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  // Verification code styles
  verifyInstructions: {
    fontSize: 14,
    color: darkGray,
    textAlign: 'center',
    margin: '0 0 16px 0',
    '& .phone-display': {
      fontWeight: 500,
      color: textPrimary,
    },
  },
  codeInputContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 16,
  },
  codeInput: {
    width: 48,
    height: 56,
    border: `1px solid ${borderColor}`,
    borderRadius: 4,
    textAlign: 'center',
    fontSize: 20,
    fontWeight: 600,
    outline: 'none',
    '&:focus': {
      borderColor: teal900,
      boxShadow: `0 0 0 1px ${teal900}`,
    },
  },
  resendLink: {
    textAlign: 'center',
    marginTop: 12,
  },
  resendLinkText: {
    fontSize: 14,
    color: teal900,
    cursor: 'pointer',
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  // Consultation summary styles
  consultationSummary: {
    backgroundColor: surfaceSecondary,
    border: `1px solid ${borderColor}`,
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  consultationSummaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  consultationSummaryLabel: {
    fontSize: 14,
    color: darkGray,
  },
  consultationSummaryValue: {
    fontSize: 14,
    fontWeight: 600,
    color: textPrimary,
  },
  consultationSummaryNote: {
    fontSize: 12,
    color: darkGray,
    margin: '8px 0 0 0',
  },
  // Authenticating loader styles
  authenticatingContainer: {
    textAlign: 'center',
    padding: '32px 0',
  },
  spinner: {
    width: 48,
    height: 48,
    border: `4px solid ${borderColor}`,
    borderTop: `4px solid ${teal900}`,
    borderRadius: '50%',
    margin: '0 auto 16px',
    animation: '$spin 1s linear infinite',
  },
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
  authenticatingTitle: {
    fontSize: 16,
    fontWeight: 500,
    color: textPrimary,
    margin: '0 0 8px 0',
  },
  authenticatingSubtitle: {
    fontSize: 14,
    color: darkGray,
    margin: 0,
  },
  // Scheduling Step styles
  schedulingContent: {
    padding: '8px 24px 16px',
    backgroundColor: surfaceElevated,
    // Consistent 16px label sizing for all form fields
    '& .MuiInputLabel-root': {
      fontSize: 16,
    },
    // Remove scale transform on shrink so label stays 16px
    '& .MuiInputLabel-shrink': {
      transform: 'translate(0, -1.5px) scale(1)',
    },
    // Add space between DurationPicker label and dropdown
    '& .MuiFormControl-root .MuiSelect-select': {
      marginTop: 12,
    },
    // Date/time picker input adornment - reduce padding
    '& .MuiInputAdornment-root': {
      marginLeft: 0,
      marginRight: 4,
    },
    // Reduce gaps between components
    '& .MuiGrid-item': {
      paddingTop: '0 !important',
    },
  },
  noteLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: textPrimary,
    marginBottom: 8,
    display: 'block',
  },
  noteTextarea: {
    width: '100%',
    padding: '12px',
    border: `1px solid ${borderColor}`,
    borderRadius: 4,
    fontSize: 14,
    resize: 'vertical',
    minHeight: 80,
    fontFamily: 'inherit',
    '&:focus': {
      outline: 'none',
      borderColor: teal900,
      boxShadow: `0 0 0 1px ${teal900}`,
    },
    '&::placeholder': {
      color: '#9CA3AF',
    },
  },
  durationSection: {
    marginTop: 16,
  },
  durationLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: textPrimary,
    marginBottom: 8,
    display: 'block',
  },
  durationButtons: {
    display: 'flex',
    gap: 12,
  },
  durationButton: {
    flex: 1,
    padding: '12px 16px',
    border: `1px solid ${borderColor}`,
    borderRadius: 4,
    backgroundColor: surfaceElevated,
    fontSize: 14,
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'center',
    '&:hover': {
      borderColor: teal900,
    },
  },
  durationButtonSelected: {
    borderColor: teal900,
    backgroundColor: '#F0FDFA',
    color: teal900,
    fontWeight: 500,
  },
  durationPrice: {
    display: 'block',
    fontSize: 12,
    color: darkGray,
    marginTop: 4,
  },
  durationPriceSelected: {
    color: teal900,
  },
  timeSlotsSection: {
    marginTop: 16,
  },
  timeSlotsLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: textPrimary,
    marginBottom: 8,
    display: 'block',
  },
  timeSlot: {
    marginBottom: 12,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  timeSlotInput: {
    width: '100%',
    padding: '12px',
    border: `1px solid ${borderColor}`,
    borderRadius: 4,
    fontSize: 14,
    cursor: 'pointer',
    backgroundColor: surfaceElevated,
    textAlign: 'left',
    '&:focus': {
      outline: 'none',
      borderColor: teal900,
      boxShadow: `0 0 0 1px ${teal900}`,
    },
    '&::placeholder': {
      color: '#9CA3AF',
    },
  },
  timeSlotExpertTime: {
    fontSize: 12,
    color: darkGray,
    marginTop: 4,
    paddingLeft: 12,
  },
  timezoneLink: {
    marginTop: 16,
  },
  timezoneLinkText: {
    fontSize: 14,
    color: teal900,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  priceSummary: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 20,
    padding: '12px 16px',
    backgroundColor: surfaceSecondary,
    border: `1px solid ${borderColor}`,
    borderRadius: 8,
  },
  priceSummaryLabel: {
    fontSize: 14,
    color: darkGray,
  },
  priceSummaryAmount: {
    fontSize: 16,
    fontWeight: 600,
    color: textPrimary,
  },
  disclaimer: {
    fontSize: 12,
    color: darkGray,
    marginTop: 12,
    lineHeight: 1.5,
  },
});
